import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { CheckCircle, Circle, ChevronDown, ChevronUp, X } from 'lucide-react'
import { formatRelativeTime } from '@/utils/timeUtils'
import { colors } from '@/styles/colors'
import { useAppStore } from '@/store'
import type { Comment } from '@/types/comment'
import { getUserDisplayName } from '@/utils/userUtils'
import { useOrganizationDirectory } from '@/hooks/useOrganizationDirectory'
import { apiService } from '@/service/api'
import CommentUserAvatar from './CommentUserAvatar'
import SharedCommentInput from './SharedCommentInput'
import ThreadHeader from '@/components/Comments/ThreadHeader'
import ReactionBar from '@/components/Comments/ReactionBar'
import ReactionPicker from '@/components/Comments/ReactionPicker'
import { useTranslation } from '@/i18n'
import { placeThreadPopover } from '../utils/threadPopoverPlacement'

interface CommentThreadPopoverProps {
  commentId: string
  isOpen: boolean
  onClose: () => void
  anchorX: number
  anchorY: number
}

const CommentThreadPopover = ({
  commentId,
  isOpen,
  onClose,
  anchorX,
  anchorY,
}: CommentThreadPopoverProps) => {
  const { t } = useTranslation()
  const {
    comments,
    user,
    createComment,
    deleteComment,
    resolveComment,
    toggleReaction,
    updateCommentInState,
  } = useAppStore()

  const [replyContent, setReplyContent] = useState('') // For main reply at bottom
  const [threadReplyContent, setThreadReplyContent] = useState('') // For inline thread reply
  const [replyMentionedUserIds, setReplyMentionedUserIds] = useState<string[]>([])
  const [threadReplyMentionedUserIds, setThreadReplyMentionedUserIds] = useState<string[]>([])
  const [showReplies, setShowReplies] = useState(true)
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(null)
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  // UI-57 follow-up: the popover is placed from its REAL height, not a guessed
  // one, so a short thread stays beside its pin and a tall one is pulled up by
  // exactly as much as it needs. The height changes as the thread loads and as
  // replies are added, and the viewport changes on resize — both re-place it.
  const [measuredHeight, setMeasuredHeight] = useState<number>()
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }))

  // Stable handler for reply in thread button
  const handleReplyInThreadClick = useCallback((commentId: string) => {
    setReplyingToCommentId(commentId)
  }, [])

  const { board } = useAppStore()
  const directory = useOrganizationDirectory()
  const workspaceId = board?.id

  // Find the comment from the store
  const findComment = (comments: Comment[]): Comment | null => {
    for (const comment of comments) {
      if (comment.id === commentId) return comment
      const found = findComment(comment.replies || [])
      if (found) return found
    }
    return null
  }

  const mainComment = workspaceId ? findComment(comments[workspaceId] || []) : null
  // The popover node exists only while the thread is open AND its comment is
  // in the store; the measuring effect below re-arms on that boundary, not on
  // every store update (each keystroke in the reply box is one).
  const isRendered = isOpen && mainComment !== null && Boolean(workspaceId)

  // Load comment thread with all replies when opening
  useEffect(() => {
    // Always reload to get the latest thread with nested replies
    // This ensures we have all replies even after page reload
    if (isOpen && commentId && workspaceId) {
      const abortController = new AbortController()
      const loadThread = async () => {
        try {
          const threadComment = await apiService.getCommentThread(commentId, abortController.signal)
          if (!abortController.signal.aborted) {
            // Update the comment in the store with the full thread data
            // This will trigger a re-render with all nested replies
            updateCommentInState(workspaceId, threadComment)
          }
        } catch (error) {
          if (!abortController.signal.aborted) {
            console.error('[CommentThreadPopover] Failed to load thread:', error)
          }
        }
      }
      loadThread()
      return () => {
        abortController.abort()
      }
    }
  }, [isOpen, commentId, workspaceId, updateCommentInState])

  useLayoutEffect(() => {
    if (!isRendered) return
    const node = popoverRef.current
    if (!node) return
    const measure = () => setMeasuredHeight(node.offsetHeight)
    measure()
    // jsdom has no ResizeObserver; the first measurement still runs there.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [isRendered])

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isOpen])

  // Handle outside clicks (with delay to avoid immediate close)
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Check if click is inside popover
      if (popoverRef.current && popoverRef.current.contains(target)) {
        return
      }

      // Check if click is inside emoji picker (it's portaled to body)
      const emojiPicker = document.querySelector('[class*="bg-white"][class*="border"][class*="rounded-lg"][class*="shadow-lg"]')
      if (emojiPicker && emojiPicker.contains(target)) {
        return
      }

      // Close if clicked outside both
      onClose()
    }

    const timeoutId = setTimeout(() => {
      // Changed from 'mousedown' to 'click' to not interfere with button clicks
      document.addEventListener('click', handleClickOutside)
    }, 150)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen || !mainComment || !workspaceId) return null

  const handleReply = async () => {
    // Determine which content to use based on context
    const content = replyingToCommentId ? threadReplyContent : replyContent
    const mentionedUserIds = replyingToCommentId ? threadReplyMentionedUserIds : replyMentionedUserIds

    if (!content.trim() || !user) {
      return
    }

    try {
      // If replying in thread, use the selected comment ID, otherwise reply to main comment
      const parentId = replyingToCommentId || commentId
      await createComment(workspaceId, {
        content: content.trim(),
        parent_comment_id: parentId,
        mentioned_user_ids: mentionedUserIds,
      })

      // Clear the appropriate content field
      if (replyingToCommentId) {
        setThreadReplyContent('')
        setThreadReplyMentionedUserIds([])
        setReplyingToCommentId(null) // Reset after successful thread reply
      } else {
        setReplyContent('')
        setReplyMentionedUserIds([])
      }
    } catch (error) {
      console.error('Failed to create reply:', error)
    }
  }

  const handleDelete = async (comment: Comment) => {
    if (!user || comment.user_id !== user.userId) return
    if (comment.id === commentId) {
      onClose()
    }
    try {
      await deleteComment(comment.id)
    } catch (error) {
      console.error('Failed to delete comment:', error)
    }
  }

  const handleResolve = async () => {
    try {
      await resolveComment(commentId, !mainComment.is_resolved)
    } catch (error) {
      console.error('Failed to resolve comment:', error)
    }
  }

  // Flatten all replies to separate direct replies from threaded replies
  const getAllReplies = (comment: Comment): Comment[] => {
    const allReplies: Comment[] = []

    const collectReplies = (replies: Comment[]) => {
      for (const reply of replies) {
        allReplies.push(reply)
        if (reply.replies && reply.replies.length > 0) {
          collectReplies(reply.replies)
        }
      }
    }

    if (comment.replies) {
      collectReplies(comment.replies)
    }

    return allReplies
  }

  const renderComment = (comment: Comment, nestingLevel: number = 0) => {
    const isThreaded = nestingLevel > 0
    const isHovered = hoveredCommentId === comment.id

    // Get all replies for this comment (only if it's a direct reply, to show threaded children)
    const allReplies = mainComment ? getAllReplies(mainComment) : []
    const childReplies = allReplies.filter(r => r.parent_comment_id === comment.id)

    const handleReactionSelect = async (emoji: string) => {
      if (!user || !workspaceId) return
      try {
        await toggleReaction(workspaceId, comment.id, emoji, user.userId)
      } catch (error) {
        console.error('Failed to add reaction:', error)
      }
    }

    return (
      <div key={comment.id}>
        <div
          className={`${isThreaded ? 'ml-12 mt-3' : nestingLevel === 0 && comment.id !== commentId ? 'px-4 py-3' : 'p-4'} relative`}
          onMouseEnter={() => setHoveredCommentId(comment.id)}
          onMouseLeave={() => setHoveredCommentId(null)}
        >
          <div className="flex gap-3">
            <CommentUserAvatar userId={comment.user_id} size={isThreaded ? 28 : 32} />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm">{getUserDisplayName(comment.user_id, user, directory)}</span>
                <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                  {formatRelativeTime(comment.created_at)}
                </span>
              </div>

              <p className="text-sm text-gray-900 dark:text-dark-text mb-2">
                {(() => {
                  // Remove last names from mentions: "@FirstName LastName" -> "@FirstName"
                  const contentWithoutLastNames = comment.content.replace(/@(\w+)\s+\w+/g, '@$1')

                  return contentWithoutLastNames.split(/(@\w+)/g).map((part, index) => {
                    // Check if this part is a mention (starts with @)
                    if (part.startsWith('@')) {
                      return <span key={index} className="font-bold text-black dark:text-white">{part}</span>
                    }
                    return <span key={index}>{part}</span>
                  })
                })()}
              </p>

              {/* Reactions Display - Always show ReactionBar */}
              {workspaceId && (
                <div className="mb-2">
                  <ReactionBar
                    commentId={comment.id}
                    workspaceId={workspaceId}
                    reactions={comment.reactions || []}
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                {comment.id !== commentId && nestingLevel === 0 && (
                  <span
                    role="button"
                    tabIndex={0}
                    onPointerDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      // Call handler immediately on pointer down to avoid re-render destroying element
                      handleReplyInThreadClick(comment.id)
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    className="text-xs text-blue-600 hover:underline font-medium cursor-pointer"
                  >
                    {t('board:comments.reply_in_thread', { defaultValue: 'Reply in thread' })}
                  </span>
                )}
                {comment.user_id === user?.userId && (
                  <button
                    type="button"
                    onPointerDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDelete(comment)
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    {t('board:comments.delete', { defaultValue: 'Delete' })}
                  </button>
                )}
              </div>
            </div>

            {/* Hover Reaction Picker */}
            {isHovered && user && (
              <div className="absolute top-2 right-2">
                <ReactionPicker
                  onReactionSelect={handleReactionSelect}
                  disabled={false}
                />
              </div>
            )}
          </div>
        </div>

        {/* Inline Reply Input - Shows when replying to this specific comment */}
        {replyingToCommentId === comment.id && (
          <div className="ms-12 mt-3 px-4">
            <div className="flex gap-2 items-center">
              <CommentUserAvatar userId={user?.userId || 'unknown'} size={28} />
              <div className="flex-1">
                <SharedCommentInput
                  value={threadReplyContent}
                  onChange={setThreadReplyContent}
                  onSubmit={handleReply}
                  onMentionedUsersChange={setThreadReplyMentionedUserIds}
                  placeholder={t('board:comments.reply_in_thread_placeholder')}
                  autoFocus={true}
                  size="small"
                />
              </div>
              <button
                type="button"
                onPointerDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setReplyingToCommentId(null)
                  setThreadReplyContent('')
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-md transition-colors"
                title={t('board:comments.cancel', { defaultValue: 'Cancel' })}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Render threaded replies (level 1 only) */}
        {/* Only render children if this is NOT the main comment AND showReplies is true */}
        {comment.id !== commentId && nestingLevel === 0 && childReplies.length > 0 && showReplies && (
          <div>
            {childReplies.map(childReply => renderComment(childReply, 1))}
          </div>
        )}
      </div>
    )
  }

  // Calculate popover position — see placeThreadPopover for the rules.
  const getPopoverStyle = (): React.CSSProperties => {
    const { left, top, width, maxHeight } = placeThreadPopover({
      anchorX,
      anchorY,
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
      measuredHeight,
    })
    return {
      position: 'fixed',
      zIndex: 10000,
      width: `${width}px`,
      maxHeight: `${maxHeight}px`,
      left: `${left}px`,
      top: `${top}px`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    }
  }

  // Get all direct replies
  const allReplies = mainComment ? getAllReplies(mainComment) : []
  const directReplies = allReplies.filter(r => r.parent_comment_id === commentId)

  // Check if any direct reply has threaded children
  const hasThreadedReplies = directReplies.some(reply => {
    const childReplies = allReplies.filter(r => r.parent_comment_id === reply.id)
    return childReplies.length > 0
  })

  return (
    <div ref={popoverRef} className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl flex flex-col" style={{ ...getPopoverStyle(), backgroundColor: 'hsl(var(--card))' }}>
      {/* Header with ThreadHeader component */}
      <ThreadHeader
        onClose={onClose}
        showNavigation={false}
      />

      {/* Resolve Status Bar */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border">
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleResolve()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          className="flex items-center gap-2 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted px-3 py-1.5 rounded-md transition-colors w-full"
        >
          {mainComment.is_resolved ? (
            <>
              <CheckCircle size={16} style={{ color: colors.success[500] }} />
              <span className="font-medium">{t('board:comments.resolved', { defaultValue: 'Resolved' })}</span>
            </>
          ) : (
            <>
              <Circle size={16} className="text-gray-400 dark:text-gray-500" />
              <span>{t('board:comments.mark_as_resolved', { defaultValue: 'Mark as resolved' })}</span>
            </>
          )}
        </button>
      </div>

      {/* Main Comment */}
      <div className="flex-1 overflow-y-auto">
        {renderComment(mainComment)}

        {/* Direct Replies - Always Visible */}
        {directReplies.length > 0 && (
          <div>
            {directReplies.map((reply) => renderComment(reply, 0))}
          </div>
        )}

        {/* Threaded Replies Section - Only show Hide button if there are threaded replies */}
        {hasThreadedReplies && (
          <div className="pb-2 px-4">
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowReplies(!showReplies)
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-dark-text py-2"
            >
              {showReplies ? (
                <>
                  <ChevronUp size={16} />
                  <span>{t('board:comments.hide_threaded_replies', { defaultValue: 'Hide threaded replies' })}</span>
                </>
              ) : (
                <>
                  <ChevronDown size={16} />
                  <span>{t('board:comments.show_threaded_replies', { defaultValue: 'Show threaded replies' })}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Reply Input - Always shown at bottom */}
      <div className="border-t p-4">
        <div className="flex gap-2 items-start">
          <CommentUserAvatar userId={user?.userId || 'unknown'} size={32} />
          <div className="flex-1">
            <SharedCommentInput
              value={replyContent}
              onChange={setReplyContent}
              onSubmit={handleReply}
              onMentionedUsersChange={setReplyMentionedUserIds}
              placeholder={t('board:comments.reply_placeholder')}
              size="regular"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CommentThreadPopover
